import React, { useEffect, useRef, useState } from 'react';

export interface IInfiniteScroll {
    children?: React.ReactNode;
    pageSize: number;
    page: number;
    fetchFunc: (page: number) => Promise<void>;
    total: number;
    current: number;
    handleSetPage: React.Dispatch<React.SetStateAction<number>>;
}

export const InfiniteScroll: React.FC<IInfiniteScroll> = ({
    children,
    pageSize = 5,
    page,
    fetchFunc,
    total,
    current,
    handleSetPage,
}: IInfiniteScroll) => {
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = async (page: number) => {
        setIsLoading(true);

        await fetchFunc(page);

        setIsLoading(false);
    };

    const observerTarget = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && page * pageSize < total && !isLoading) {
                    handleSetPage((prevPage) => {
                        const nextPage = prevPage + 1;
                        fetchData(nextPage);
                        return nextPage;
                    });
                }
            },
            { threshold: 1 },
        );
        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }
        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [observerTarget, page, current, total, isLoading, pageSize]);

    return (
        <>
            {children}
            {current > 0 && !isLoading ? <div ref={observerTarget}></div> : null}
        </>
    );
};
